import { cva, type VariantProps } from 'class-variance-authority';
import NumberFlow, { continuous } from '@number-flow/react';
import { cn } from '@/lib/utils';
import { ReactNode } from 'react';

const badgeVariants = cva(
    'inline-flex flex-row items-center justify-center rounded-[4px] gap-1 py-0.5 px-1.5 min-h-[22px]',
    {
        variants: {
            variant: {
                base: 'bg-grey-550 text-grey-200 backdrop-blur-[20px]',
                green: 'bg-green-dark text-green-light backdrop-blur-[20px]',
                neutral: 'bg-grey-500 text-success backdrop-blur-[20px]',
                positive:
                    'bg-success-100/25 text-success-100 backdrop-blur-[20px]',
                warning:
                    'bg-warning-100/25 text-warning-100 backdrop-blur-[20px]',
                negative:
                    'bg-danger-300/25 text-danger-300 backdrop-blur-[20px]',
                'positive-glass':
                    'bg-black/40 text-success-100 backdrop-blur-[20px]',
                'warning-glass':
                    'bg-black/40 text-warning-100 backdrop-blur-[20px]',
                'negative-glass':
                    'bg-black/40 text-danger-300 backdrop-blur-[20px]',
                'neutral-glass':
                    'bg-black/40 text-grey-100 backdrop-blur-[20px]',
                'white-glass':
                    'bg-black/40 text-white-100 backdrop-blur-[20px]',
                'license-plate':
                    'bg-black/40 text-white-100 backdrop-blur-[20px] px-2 py-1',
                number: 'bg-grey-500 text-grey-200 min-w-3.5 min-h-3.5 h-3.5 p-0.5 [&>*]:h-full [&>*]:min-h-0 [&>*]:flex [&>*]:items-center [&>*]:justify-center',
                numberDark:
                    'bg-grey-700 text-grey-200 min-w-3.5 min-h-3.5 h-3.5 p-0.5 [&>*]:h-full [&>*]:min-h-0 [&>*]:flex [&>*]:items-center [&>*]:justify-center',
                iconSquare:
                    'size-4 min-h-0 p-0.5 rounded-[2px] bg-grey-550 text-grey-200 backdrop-blur-[20px]',
                dot: 'w-[5px] h-[5px] min-h-0 p-0 rounded-full bg-grey-25',
                analyzing: 'bg-black/40 text-grey-100 backdrop-blur-[20px]',
                pending: 'bg-black/40 text-grey-100 backdrop-blur-[20px]',
                error: 'bg-black/40 text-danger-200 backdrop-blur-[20px]',
            },
        },
        defaultVariants: {
            variant: 'base',
        },
    },
);

export type BadgeVariant = VariantProps<typeof badgeVariants>['variant'];

const textStyles: Partial<Record<NonNullable<BadgeVariant>, string>> = {
    'license-plate': 'font-geist-mono text-[12px] font-normal leading-[18px]',
    number: 'font-geist-mono text-[10px] font-normal leading-4',
    numberDark: 'font-geist-mono text-[10px] font-normal leading-4',
};

const DEFAULT_TEXT_STYLE = 'text-xs-medium';

interface BadgeProps extends VariantProps<typeof badgeVariants> {
    value?: string | number;
    children?: ReactNode;
    className?: string;
    animated?: boolean;
}

export function Badge({
    value,
    variant,
    children,
    className,
    animated = false,
}: BadgeProps) {
    const content = children ?? value;
    const resolvedVariant = variant ?? 'base';

    if (resolvedVariant === 'dot') {
        return (
            <span
                className={cn(badgeVariants({ variant: 'dot' }), className)}
            />
        );
    }

    const numberFlowTypography =
        textStyles[resolvedVariant] ?? DEFAULT_TEXT_STYLE;

    if (
        animated &&
        (resolvedVariant === 'number' || resolvedVariant === 'numberDark') &&
        typeof value === 'number'
    ) {
        return (
            <div
                className={cn(
                    badgeVariants({ variant: resolvedVariant }),
                    className,
                )}
            >
                <NumberFlow
                    value={value}
                    locales="en-US"
                    plugins={[continuous]}
                    className={numberFlowTypography}
                />
            </div>
        );
    }

    return (
        <div
            className={cn(
                badgeVariants({ variant: resolvedVariant }),
                className,
            )}
        >
            {typeof content === 'string' || typeof content === 'number' ? (
                <span className={numberFlowTypography}>{content}</span>
            ) : (
                content
            )}
        </div>
    );
}
